import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {Form} from 'formik';
import * as Yup from 'yup';
import {List} from '@tickchak/react-share/app';
import {TShow} from '../../../../../hooks/useShow';
import Input from '../../../../../component/input/Input';
import FormContext from '../../../../components/formContext/FormContext';
import BackArrow from '../../../../../assets/icons/BackArrow';
import {createHall} from '../../../../../redux/timetable/timetableActions';
import {bounceMessage} from '../../../../../layout/userMessages/UserMessage';
import * as css from './popupCard.css';

interface AddHallPopupProps {
    show: TShow;
    onAdd: (thid: number) => void;
}

const HALL_NAME_MAX = 30;

const AddHallPopup: FC<AddHallPopupProps> = ({show, onAdd}) => {
    const {t} = useTranslation('timetable');
    if(!show.isOpen) return null;

    const handleClose = () => show.close();

    const validationSchema = Yup.object({
        title: Yup.string()
            .trim()
            .matches(/^(?!.*[<>])(?![!@#$%^&(),.?":{}|[\]`\\/~\s]*$)(.|[\r\n])+$/, t('global:errorTemplate'))
            .min(2, t('nameTooShort', {min: 2}))
            .max(HALL_NAME_MAX, t('nameTooLong', {max: HALL_NAME_MAX}))
            .required(t('fieldRequired')),
    });

    const handleSubmit = async (_changed: unknown, {title}: {title: string}) => {
        const hall = await createHall(title.trim());
        if(hall) onAdd(hall.thid);
        handleClose();
    };

    const handleAdd = (title: string, submitForm: () => void) => {
        if(title.trim().length > HALL_NAME_MAX) {
            bounceMessage(t('hallNameTooLong', {max: HALL_NAME_MAX}), 'warning');
            return;
        }
        submitForm();
    };

    return (
        <FormContext initialValues={{title: ''}} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {/* eslint-disable-next-line react-redux/no-unused-prop-types */}
            {({submitForm, values}: { submitForm: () => void; values: {title: string}}) => (
                <List
                    show={show}
                    data-cy="addHallPop"
                    position={{width: '375px', height: '432px'}}
                    ariaLabel={t('addHallTitle')}
                >
                    <List.Header
                        fixed
                        title={t('addHallTitle')}
                        onBack={handleClose}
                        backIcon={<BackArrow />}
                    />
                    <List.Items>
                        <css.card>
                            <Form placeholder="" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
                                <Input
                                    name="title"
                                    text={t('addHallInputLabel')}
                                    icon="hallOutline"
                                    dataCy="addHallInput"
                                    events={{}}
                                    className=""
                                    forwardRef={null}
                                />
                            </Form>
                        </css.card>
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('cancel')} size="category" onClick={handleClose} />
                        <List.Footer.Button
                            text={t('add')}
                            variant="brand"
                            size="category"
                            onClick={() => handleAdd(values.title, submitForm)}
                        />
                    </List.Footer>
                </List>
            )}
        </FormContext>
    );
};

export default AddHallPopup;
